// Copyright 2016 Sorama B.V.

import { Frequency } from "units/Frequency";
import { Unit, UnitValue } from "units/Unit";

export interface Weighting {
    name: string;

    getWeight(xValue: UnitValue, yUnit: Unit, lowerBound?: UnitValue): number;

    equals(other: Weighting): boolean;
}

export class PolygonWeighting implements Weighting {
    private weights: number[];
    private xAxisUnit: Unit;

    constructor(weigths: number[], xAxisUnit: Unit) {
        this.weights = weigths;
        this.xAxisUnit = xAxisUnit;
    }

    public get name(): string {
        return "PolygonWeighting";
    }

    public getWeight(
        xValue: UnitValue,
        yUnit: Unit,
        lowerBound: UnitValue = new UnitValue(0, Frequency.hertz)
    ): number {
        if (!yUnit.isLinearUnit) throw new Error("Non linear unit weighting is not supported");

        const x = xValue.toUnit(this.xAxisUnit);
        if (x < lowerBound.toUnit(this.xAxisUnit)) return 0;

        let xx = 1;
        let scale = 0;

        for (let i = 0; i < this.weights.length; i++) {
            scale += this.weights[i] * xx;
            xx *= x;
        }

        return scale;
    }

    public equals(other: Weighting): boolean {
        throw new Error("PolygonWeighting.equals: Not Implemented");
    }
}

export abstract class CustomWeighting implements Weighting {
    private xAxisUnit: Unit;
    private _name: string;

    constructor(name: string, xAxisUnit: Unit) {
        this._name = name;
        this.xAxisUnit = xAxisUnit;
    }

    public get name(): string {
        return this._name;
    }

    public getWeight(
        xValue: UnitValue,
        yUnit: Unit,
        lowerBound: UnitValue = new UnitValue(0, Frequency.hertz)
    ): number {
        if (!yUnit.isLinearUnit) throw new Error("Non linear unit weighting is not supported");

        const x = xValue.toUnit(this.xAxisUnit);

        if (x < lowerBound.toUnit(this.xAxisUnit)) return 0;

        return this.convert(x);
    }

    protected abstract convert(x: number): number;

    public equals(other: Weighting): boolean {
        const customOther = <CustomWeighting>other;

        if (!customOther) return;

        return customOther._name === this._name && customOther.xAxisUnit === this.xAxisUnit;
    }
}

/**
 * A weighting that does not change values
 * Similar to Z-weighting except the unit is not tied to Frequency
 */
export class NoneWeighting implements Weighting {
    public name = "None";

    public getWeight(xValue: UnitValue, yUnit: Unit, lowerBound?: UnitValue): number {
        return 1.0;
    }

    public equals(other: Weighting): boolean {
        return other.name === this.name;
    }
}

export class ZWeighting extends CustomWeighting {
    constructor() {
        super("Z", Frequency.hertz);
    }

    protected convert(f: number): number {
        return 1.0;
    }
}

export class AWeighting extends CustomWeighting {
    //#region constants for AWeight function

    private static V0 = Math.pow(10, 2.0 / 20.0) * Math.pow(12200.0, 2.0);
    private static V1 = Math.pow(20.6, 2.0);
    private static V2 = Math.pow(107.65265, 2.0);
    private static V3 = Math.pow(737.86223, 2.0);
    private static V4 = Math.pow(12200.0, 2.0);

    //#endregion

    constructor() {
        super("A", Frequency.hertz);
    }

    protected convert(f: number): number {
        const fSquared = f * f;

        const num = AWeighting.V0 * fSquared * fSquared;
        const den =
            (fSquared + AWeighting.V1) *
            Math.sqrt((fSquared + AWeighting.V2) * (fSquared + AWeighting.V3)) *
            (fSquared + AWeighting.V4);

        return num / den;
    }
}

export class BWeighting extends CustomWeighting {
    //#region constants for BWeight function

    private static V0 = Math.pow(10, 0.17 / 20.0) * Math.pow(12200.0, 2.0);
    private static V1 = Math.pow(20.6, 2.0);
    private static V2 = Math.pow(158.5, 2.0);
    private static V3 = Math.pow(12200.0, 2.0);

    //#endregion

    constructor() {
        super("B", Frequency.hertz);
    }

    protected convert(f: number): number {
        const fSquared = f * f;
        const num = BWeighting.V0 * f * fSquared;
        const den = (fSquared + BWeighting.V1) * Math.sqrt(fSquared + BWeighting.V2) * (fSquared + BWeighting.V3);

        return num / den;
    }
}

export class CWeighting extends CustomWeighting {
    //#region constants for BWeight function

    private static V0 = Math.pow(10, 0.06 / 20) * Math.pow(12200.0, 2.0);
    private static V1 = Math.pow(20.6, 2.0);
    private static V2 = Math.pow(12200.0, 2.0);

    //#endregion

    constructor() {
        super("C", Frequency.hertz);
    }

    protected convert(f: number): number {
        const fSquared = f * f;
        const num = CWeighting.V0 * fSquared;
        const den = (fSquared + CWeighting.V1) * (fSquared + CWeighting.V2);

        return num / den;
    }
}
